import { useState, useEffect, useCallback } from 'react'; // Ajouter useCallback
import { useParams } from 'react-router-dom';
import { UseCaseDetail } from '../types'; // Ajustez le chemin si nécessaire
import useCaseService from '../services/useCaseService'; // Importer le client API
import i18n from '../i18n/config';

interface UseUseCaseDataResult {
  useCase: UseCaseDetail | null | undefined; // undefined: loading, null: not found, UseCaseDetail: found
  isLoading: boolean;
  error: string | null;
  /** Raw error of the last failed fetch (lets the page tell a 404 of another org). */
  loadError: unknown;
  refetchUseCase: () => Promise<void>; // Ajouter la fonction de rechargement
}

export const useUseCaseData = (): UseUseCaseDataResult => {
  const { id } = useParams<{ id: string }>();
  const [useCase, setUseCase] = useState<UseCaseDetail | null | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);

  // Extraire la logique de fetch dans une fonction réutilisable avec useCallback
  const fetchUseCase = useCallback(async () => {
    if (!id) {
      setError(i18n.t('errors.useCase.missingIdInUrl'));
      setIsLoading(false);
      setUseCase(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    setLoadError(null);
    setUseCase(undefined); // Reset state for loading

    try {
      console.log(`useUseCaseData: Fetching data for ID: ${id} from API...`);
      const data = await useCaseService.get(id);
      setUseCase(data);
      console.log(`useUseCaseData: Successfully fetched data for ID: ${id}`);
    } catch (err: any) {
      console.error(`useUseCaseData: Error fetching use case ${id}:`, err);
      setLoadError(err);
      if (err.response && err.response.status === 404) {
        setError(i18n.t('errors.useCase.notFoundWithId', { id }));
        setUseCase(null); // Définir comme non trouvé
      } else {
        setError(err.message || i18n.t('errors.useCase.loadFailed'));
        setUseCase(null); // Erreur générique, définir comme non trouvé aussi
      }
    } finally {
      setIsLoading(false);
    }
  }, [id]); // Dépendance à l'ID

  // Appeler fetchUseCase au montage et quand l'ID change
  useEffect(() => {
    fetchUseCase();
  }, [fetchUseCase]); // Utiliser la fonction mémorisée

  // Retourner la fonction de rechargement
  return { useCase, isLoading, error, loadError, refetchUseCase: fetchUseCase };
};
